import {
  getStores,
  hasSDK,
  type Report,
  ReportStatus,
  useStores,
} from '@littlebird/sdk';
import {
  getRunUserWait,
  isRunWaiting,
  useRoutineRefresh as useSharedRoutineRefresh,
} from '@littlebird/ui-utils';
import { makeAutoObservable, runInAction } from 'mobx';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

class RoutineReadState {
  hasLoadedRoutines = false;
  /** A read failed with nothing to show, so the UI can offer a retry instead of
   * a skeleton until the next poll. Cleared by the next success */
  hasFailedRoutines = false;
  hasLoadedReports = false;
  hasLoadedLibrary = false;
  hasLoadedSuggestions = false;

  constructor() {
    makeAutoObservable(this);
  }
}

export const routineReadState = new RoutineReadState();

const inFlightFetches = new Map<string, Promise<void>>();

// Bumped on SDK logout so promises started for the previous account can't
// mark state as loaded after resetRoutineReadState clears it.
let fetchGeneration = 0;

/**
 * A failed read retries on its own instead of waiting out the poll, which is
 * five minutes for routines and one for reports. Bounded, so a backend that is
 * genuinely down is not hammered; after these the normal poll takes over, and
 * the routines list offers a Retry in the meantime.
 */
const RETRY_DELAYS_MS = [3_000, 10_000, 30_000];

const retryAttempts = new Map<string, number>();
const retryTimers = new Map<string, ReturnType<typeof setTimeout>>();

const scheduleRetry = (
  key: string,
  run: () => Promise<unknown>,
  markSettled: (loaded: boolean) => void
) => {
  const attempt = retryAttempts.get(key) ?? 0;
  const delay = RETRY_DELAYS_MS[attempt];
  if (delay === undefined || retryTimers.has(key)) return;

  retryAttempts.set(key, attempt + 1);
  const generation = fetchGeneration;
  retryTimers.set(
    key,
    setTimeout(() => {
      retryTimers.delete(key);
      if (generation !== fetchGeneration) return;
      void fetchOnce(key, run, markSettled);
    }, delay)
  );
};

const fetchOnce = (
  key: string,
  run: () => Promise<unknown>,
  markSettled: (loaded: boolean) => void
): Promise<void> => {
  const existing = inFlightFetches.get(key);
  if (existing) return existing;

  const generation = fetchGeneration;
  const request = run()
    .then(
      () => {
        retryAttempts.delete(key);
        if (generation === fetchGeneration)
          runInAction(() => markSettled(true));
      },
      () => {
        if (generation !== fetchGeneration) return;
        runInAction(() => markSettled(false));
        scheduleRetry(key, run, markSettled);
      }
    )
    .finally(() => {
      // Only if it is still ours: a reset clears the map, and a fresh request
      // may already hold this key by the time a stale one settles.
      if (inFlightFetches.get(key) === request) inFlightFetches.delete(key);
    });
  inFlightFetches.set(key, request);
  return request;
};

/** Call after SDK logout: clears read state and in-flight promises so the next
 * account does not inherit either */
export const resetRoutineReadState = () => {
  fetchGeneration += 1;
  inFlightFetches.clear();
  for (const timer of retryTimers.values()) clearTimeout(timer);
  retryTimers.clear();
  retryAttempts.clear();
  runInAction(() => {
    routineReadState.hasLoadedRoutines = false;
    routineReadState.hasFailedRoutines = false;
    routineReadState.hasLoadedReports = false;
    routineReadState.hasLoadedLibrary = false;
    routineReadState.hasLoadedSuggestions = false;
  });
};

const refreshNow = (
  run: () => Promise<unknown>,
  markSettled: (loaded: boolean) => void
) => {
  void run().then(
    () => runInAction(() => markSettled(true)),
    () => runInAction(() => markSettled(false))
  );
};

/** Shared by every routines read so a later failure cannot undo a good load. */
const markRoutinesSettled = (loaded: boolean) => {
  if (loaded) routineReadState.hasLoadedRoutines = true;
  routineReadState.hasFailedRoutines = !loaded;
};

export const refreshRoutineReads = () => {
  if (!hasSDK()) return;
  const { routines, reports } = getStores();
  refreshNow(() => routines.fetchRoutines(), markRoutinesSettled);
  refreshNow(
    () => reports.fetchReports(),
    (loaded) => {
      if (loaded) routineReadState.hasLoadedReports = true;
    }
  );
};

const subscribeToWindowFocus = (onForeground: () => void) => {
  window.addEventListener('focus', onForeground);
  return () => window.removeEventListener('focus', onForeground);
};

const createRefreshLeaderHook = () => {
  const listeners = new Set<() => void>();
  const candidates = new Set<object>();
  let leader: object | null = null;

  const elect = () => {
    const next = candidates.values().next().value ?? null;
    if (next === leader) return;
    leader = next;
    for (const onLeaderChange of listeners) onLeaderChange();
  };

  const subscribe = (onLeaderChange: () => void) => {
    listeners.add(onLeaderChange);
    return () => {
      listeners.delete(onLeaderChange);
    };
  };

  return function useRefreshLeader(enabled: boolean): boolean {
    const instance = useRef({}).current;
    const currentLeader = useSyncExternalStore(subscribe, () => leader);

    useEffect(() => {
      if (!enabled) return;

      candidates.add(instance);
      elect();
      return () => {
        candidates.delete(instance);
        if (leader === instance) elect();
      };
    }, [enabled, instance]);

    return currentLeader === instance;
  };
};

const useIsRoutinesRefreshLeader = createRefreshLeaderHook();
const useIsReportsRefreshLeader = createRefreshLeaderHook();

interface RoutineRefreshOptions {
  reports?: boolean;
}

export const useRoutineRefresh = (
  enabled = true,
  { reports: pollReports = true }: RoutineRefreshOptions = {}
): void => {
  const { routines, reports } = useStores();
  const isRoutinesLeader = useIsRoutinesRefreshLeader(enabled);
  const isReportsLeader = useIsReportsRefreshLeader(enabled && pollReports);

  const refreshRoutines = () =>
    void fetchOnce(
      'routines',
      () => routines.fetchRoutines(),
      markRoutinesSettled
    );
  const refreshReports = () =>
    void fetchOnce(
      'reports',
      () => reports.fetchReports(),
      (loaded) => {
        if (loaded) routineReadState.hasLoadedReports = true;
      }
    );

  useSharedRoutineRefresh({
    refreshRoutines,
    refreshReports,
    hasGeneratingReport:
      isReportsLeader &&
      reports.orderedReports.some(
        // A waiting run can wait for hours; the routines_updated push covers its end
        (report) =>
          report.parseStatus(report.text.trim()) === ReportStatus.GENERATING &&
          !isRunWaiting(report) &&
          getRunUserWait(report) === null
      ),
    enabled: enabled && isRoutinesLeader,
    reportsEnabled: isReportsLeader,
    subscribeToForeground: subscribeToWindowFocus,
  });

  useEffect(() => {
    if (!enabled) return;
    if (!isRoutinesLeader && !routineReadState.hasLoadedRoutines)
      refreshRoutines();
    if (pollReports && !isReportsLeader && !routineReadState.hasLoadedReports)
      refreshReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, pollReports, isRoutinesLeader, isReportsLeader]);
};

export const hasRoutineReportDetail = (report: Report): boolean =>
  report.hasDetailText;

interface FailedReportVersion {
  id: number;
  updatedAtMs: number;
}

/**
 * A completed report's `updatedAt` never changes again, so a failure keyed on it
 * alone would suppress the detail fetch for as long as the screen stays open,
 * leaving only the 500-character preview. Forget the failure after this so a
 * transient one costs a wait rather than the report body.
 */
const REPORT_DETAIL_RETRY_MS = 30_000;

export const useRoutineReportDetail = (report: Report | undefined): boolean => {
  // React Compiler memoizes hasRoutineReportDetail(report) on the stable MobX
  // model, so detail arrival can leave the loading skeleton stuck.
  'use no memo';
  const { reports } = useStores();
  const referenceId = report?.referenceId;
  const updatedAtMs = report?.updatedAt.getTime();
  const [failedVersion, setFailedVersion] =
    useState<FailedReportVersion | null>(null);
  const isDetailPending =
    report !== undefined && !hasRoutineReportDetail(report);
  const isFailedVersion =
    failedVersion !== null &&
    failedVersion.id === referenceId &&
    failedVersion.updatedAtMs === updatedAtMs;

  useEffect(() => {
    if (referenceId === undefined || !isDetailPending) return;
    if (isFailedVersion) return;

    let cancelled = false;
    void reports.fetchByReferenceId(referenceId).then((detail) => {
      if (!cancelled && !detail?.hasDetailText && updatedAtMs !== undefined) {
        setFailedVersion({ id: referenceId, updatedAtMs });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [reports, referenceId, updatedAtMs, isDetailPending, isFailedVersion]);

  useEffect(() => {
    if (!isFailedVersion) return;
    const timer = setTimeout(
      () => setFailedVersion(null),
      REPORT_DETAIL_RETRY_MS
    );
    return () => clearTimeout(timer);
  }, [isFailedVersion]);

  return isDetailPending && !isFailedVersion;
};

export const useRoutineLibrary = (enabled = true) => {
  const { routines } = useStores();

  useEffect(() => {
    if (!enabled) return;
    void fetchOnce(
      'routine-library',
      () => routines.fetchRoutineLibrary(),
      (loaded) => {
        if (loaded) routineReadState.hasLoadedLibrary = true;
      }
    );
  }, [enabled, routines]);

  const isError = routines.getOperationError('library') !== null;
  return {
    items: routines.routineLibrary,
    tabs: routines.routineLibraryTabs,
    isLoading: !routineReadState.hasLoadedLibrary && !isError,
    isError,
  };
};

/**
 * The user's webhook endpoints, refetched on each mount so one created on
 * another device shows up the next time the routine editor opens.
 */
export const useWebhookEndpoints = (enabled = true) => {
  const { routines } = useStores();

  useEffect(() => {
    if (!enabled) return;
    // The store logs a failure; the picker then offers only "New endpoint"
    routines.fetchWebhookEndpoints().catch(() => {});
  }, [enabled, routines]);

  return routines.webhookEndpoints;
};

/** Backend-generated routine suggestions, fetched once per session. */
export const useRoutineSuggestions = (enabled = true) => {
  const { routines } = useStores();

  useEffect(() => {
    if (!enabled) return;
    void fetchOnce(
      'routine-suggestions',
      () => routines.fetchRoutineSuggestions(),
      (loaded) => {
        if (loaded) routineReadState.hasLoadedSuggestions = true;
      }
    );
  }, [enabled, routines]);

  const isError = routines.getOperationError('suggestions') !== null;
  return {
    suggestions: routines.routineSuggestions,
    isLoading: !routineReadState.hasLoadedSuggestions && !isError,
  };
};
